/*
 * recharge-buy-box-sync.js
 * Holistic Goddess - Purity theme
 *
 * Keeps the theme's Add to Cart button in sync with the ReCharge subscription widget.
 *
 * Why this is needed
 * ------------------
 * The theme renders the button label as "Add to Cart - <price>", where the price lives in
 * <span class="total-price__detail" data-total-price="<unit price in cents>">.
 * theme.js only recalculates that value from the quantity stepper (QuantityInput.updateTotalPrice),
 * and the `data-total-price` attribute is printed once by Liquid at the one-time price.
 * ReCharge rewrites the main product price when a plan is chosen, but knows nothing about the
 * theme's button, so the button keeps showing the one-time price.
 *
 * It also fixes a second problem: ReCharge injects its hidden <input name="selling_plan"> into the
 * main product form only. The sticky Add to Cart bar has no such input, so adding from the sticky
 * bar silently created a ONE-TIME purchase even with "Subscribe & save" selected.
 *
 * What it does
 * ------------
 * 1. Reads the selected selling plan from the main product form (ReCharge's own hidden input -
 *    the value that actually gets submitted, so we can never disagree with what ReCharge does).
 * 2. Mirrors that plan into every other add-to-cart form in the same section (the sticky bar).
 * 3. Looks up the real per-delivery price for that plan from /products/<handle>.js and rewrites
 *    `data-total-price` + the button label in every form in the section.
 *
 * Because it rewrites `data-total-price`, the theme's own quantity handler keeps working
 * unchanged and multiplies from the corrected unit price.
 *
 * Safe by design: if anything cannot be resolved it does nothing and the theme behaves as before.
 */
(function () {
  'use strict';

  var productCache = {};

  /* ---------- money ---------- */

  function moneyFormat() {
    try {
      return (window.themeGlobalVariables.settings.money_format) || '${{amount}}';
    } catch (e) {
      return '${{amount}}';
    }
  }

  function separate(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) cents = 0;
    var parts = (cents / 100.0).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  /* Mirrors Shopify's standard money filters so output matches the rest of the theme. */
  function formatMoney(cents, format) {
    format = format || '${{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = separate(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator':
        value = separate(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = separate(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = separate(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator':
        value = separate(cents, 0, ' ', '.'); break;
      case 'amount_with_space_separator':
        value = separate(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator':
        value = separate(cents, 2, ' ', '.'); break;
      default:
        value = separate(cents, 2, ',', '.');
    }
    return format.replace(match[0], value);
  }

  /* ---------- product data ---------- */

  function handleForScope(scope) {
    var link = scope.querySelector('a[href*="/products/"]');
    if (link) {
      var href = link.getAttribute('href') || '';
      var part = href.split('/products/')[1];
      if (part) return part.split('?')[0].split('#')[0];
    }
    if (location.pathname.indexOf('/products/') !== -1) {
      return location.pathname.split('/products/')[1].split('?')[0];
    }
    return null;
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  /* ---------- dom helpers ---------- */

  function cartForms(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll('form[action*="/cart/add"]'))
      .filter(function (f) { return f.querySelector('.total-price__detail'); });
  }

  /* The form ReCharge actually writes its hidden selling_plan input into. */
  function primaryForm(forms) {
    for (var i = 0; i < forms.length; i++) {
      if (!forms[i].classList.contains('form-sticky-add-cart')) return forms[i];
    }
    return forms[0] || null;
  }

  function selectedPlan(form) {
    if (!form) return null;
    var el = form.querySelector('[name="selling_plan"]');
    if (!el || !el.value) return null;
    var id = Number(el.value);
    return id ? id : null;
  }

  /* Keep the sticky bar submitting the same plan as the main form. */
  function mirrorPlan(form, planId) {
    var existing = form.querySelector('input[type="hidden"][name="selling_plan"][data-rc-mirrored]');
    if (planId === null) {
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }
    if (form.querySelector('[name="selling_plan"]:not([data-rc-mirrored])')) return; // ReCharge owns it
    if (!existing) {
      existing = document.createElement('input');
      existing.type = 'hidden';
      existing.name = 'selling_plan';
      existing.setAttribute('data-rc-mirrored', '');
      form.appendChild(existing);
    }
    if (existing.value !== String(planId)) existing.value = String(planId);
  }

  function quantityOf(form) {
    var q = form.querySelector('quantity-input input, [name="quantity"]');
    var n = q ? Number(q.value) : 1;
    return n > 0 ? n : 1;
  }

  function unitPriceFor(product, variantId, planId) {
    var variant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === variantId) { variant = product.variants[i]; break; }
    }
    if (!variant) variant = product.variants[0];
    if (!variant) return null;
    if (planId) {
      var allocations = variant.selling_plan_allocations || [];
      for (var j = 0; j < allocations.length; j++) {
        if (allocations[j].selling_plan_id === planId) {
          return allocations[j].per_delivery_price;
        }
      }
    }
    return variant.price;
  }

  function paint(form, unitPrice) {
    var qty = quantityOf(form);
    var text = formatMoney(unitPrice * qty, moneyFormat());
    var nodes = form.querySelectorAll('.total-price__detail');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      /* Only write when the value actually changes, so the MutationObserver cannot loop. */
      if (el.getAttribute('data-total-price') !== String(unitPrice)) {
        el.setAttribute('data-total-price', String(unitPrice));
      }
      if (el.textContent.trim() !== text) el.textContent = text;
      var button = el.closest('button[name="add"]');
      if (button) {
        var label = button.querySelector('.btn-label');
        if (label) button.setAttribute('aria-label', label.textContent.replace(/\s+/g, ' ').trim());
      }
    }
  }

  /* ---------- main ---------- */

  function syncScope(scope) {
    var forms = cartForms(scope);
    if (!forms.length) return;

    var primary = primaryForm(forms);
    var planId = selectedPlan(primary);

    var handle = handleForScope(scope);
    if (!handle) return;

    getProduct(handle).then(function (product) {
      if (!product || !product.variants || !product.variants.length) return;
      forms.forEach(function (form) {
        if (form !== primary) mirrorPlan(form, planId);
        var idInput = form.querySelector('[name="id"]');
        var variantId = idInput ? Number(idInput.value) : product.variants[0].id;
        var unit = unitPriceFor(product, variantId, planId);
        if (unit === null || unit === undefined) return;
        paint(form, unit);
      });
    });
  }

  function scopes() {
    var found = document.querySelectorAll('[id^="shopify-section"]');
    return Array.prototype.slice.call(found).filter(function (s) {
      return s.querySelector('form[action*="/cart/add"] .total-price__detail');
    });
  }

  var pending = null;
  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(function () { scopes().forEach(syncScope); }, 80);
  }

  function onInteraction(event) {
    var t = event.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-widget-container]') ||
        /^selling_plan/.test(t.name || '') ||
        t.closest('variant-selects, .product-variants, [name="id"]') ||
        /* The theme syncs the sticky bar's quantity input but not its button price,
           so repaint both forms whenever quantity changes. */
        t.closest('quantity-input') ||
        t.name === 'quantity') {
      schedule();
    }
  }

  function start() {
    document.addEventListener('change', onInteraction, true);
    document.addEventListener('click', onInteraction, true);

    /* ReCharge renders async and re-renders on every change; watch its container only. */
    var observer = new MutationObserver(schedule);
    function observeWidgets() {
      var widgets = document.querySelectorAll('[data-widget-container]');
      for (var i = 0; i < widgets.length; i++) {
        observer.observe(widgets[i], {
          subtree: true, childList: true, attributes: true,
          attributeFilter: ['value', 'checked', 'class']
        });
      }
    }
    observeWidgets();

    /* The widget may not exist yet on first paint; retry briefly, then settle. */
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      observeWidgets();
      schedule();
      if (tries >= 10) clearInterval(poll);
    }, 500);

    document.addEventListener('shopify:section:load', function () { observeWidgets(); schedule(); });
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
